import React from "react";

// The Recipe Planning table: its arithmetic and the table itself, so the screens that show it
// show the same thing. Recipe Planning is where it comes from; RM Recipe Planning (Event) asks
// for the same table over a date range, and the client's sheet is one sheet for both.
//
// What differs between the two screens is only what they measure against, which arrives as
// arguments: the Estimated RM Qty of the slot (a day, or a whole range), and the ras the
// standard column is scaled by.

export const REMARK_NAMES = ["receipe_remarks", "process_remarks", "test_remarks", "planning_remarks"];

export const dec3 = (v) => (v === null || v === undefined || isNaN(v) ? '' : Number(v).toFixed(3));

// TEMPORARY -- one line per row saying what 100 Persons QTY was worked out from. Remove once the
// figures on the client's screen have been accounted for.
//
//   l     Planned RM Qty         (the figure being inverted)
//   s     100 Persons STD QTY    = ras * qty per 100 kgs / 100
//   req   Req. RM Qty            (pinned if the row carries one, else est * s / total)
//   est   Estimated RM Qty       (the day's planned_rm_qty)
//   total the section's 100 Persons STD QTY total
//   out   100 Persons QTY        = l * s / req
const debugPersons = (row, f) => {
  /* eslint-disable no-console */
  console.log(
    `[100 Persons QTY] ${row?.name ?? '?'} (id ${row?.ingredient_id ?? '?'}) [${f.branch}] ` +
    `planned l=${f.l} x std s=${f.s} / req=${f.req} = ${f.out}   |   est=${f.est} sectionStdTotal=${f.total} ` +
    `stdQty(qty per 100kg)=${row?.stdQty} saved persons_qty=${row?.persons_qty ?? 'null'} saved req_rm_qty=${row?.req_rm_qty ?? 'null'}`
  );
};

// ---- The screen's arithmetic, defined once ----
//
// The table renders these and the edit handlers have to compute the same figures to answer an
// edit, so they are built together here rather than inside the table's own render. Two copies
// would be two things to keep in step.
export const planningMath = ({ rows, estRM, rasValue }) => {
  const est = parseFloat(estRM) || 0;
  const ras = parseFloat(rasValue);
  const hasRas = !isNaN(ras) && ras !== 0;

  // The ingredient rows in the order the table shows them: main items first, then Qty/100kg
  // descending, matching Recipe Master. The change handlers are given an index into THIS list,
  // never into the screen's own row list -- the two orders are not the same.
  const ingredientRows = (rows ?? [])
    .filter((item) => item && !REMARK_NAMES.includes(item.name))
    .slice()
    .sort((a, b) => {
      if (!!a.is_main !== !!b.is_main) return a.is_main ? -1 : 1;
      return (parseFloat(b.stdQty) || 0) - (parseFloat(a.stdQty) || 0);
    });

  // "100 Persons STD QTY" -- that ras against the ingredient's qty per 100 kgs. Null, shown
  // blank, when no ras is available: a column of zeroes would read as a measurement rather than
  // as an absence.
  const stdPersonsQty = (row) => {
    const q = parseFloat(row?.stdQty);
    if (!hasRas || isNaN(q)) return null;
    return ras * (q / 100);
  };

  // "100 Persons QTY" -- editable, and defaults to the STD figure beside it.
  const personsQty = (row) => {
    if (row?.persons_qty !== null && row?.persons_qty !== undefined && row?.persons_qty !== '') {
      const v = parseFloat(row.persons_qty);
      if (!isNaN(v)) return v;
    }
    return stdPersonsQty(row);
  };

  // The total of the 100 Persons STD QTY column for the row's own section. Main items and other
  // items are totalled separately -- the same split % Weightage divides by, and the one the table
  // shows a Total row for.
  const sectionStdTotal = (row) => ingredientRows
    .filter((r) => !!r.is_main === !!row?.is_main)
    .reduce((t, r) => t + (stdPersonsQty(r) ?? 0), 0);

  // Req. RM Qty = Estimated RM Qty * (100 Persons QTY / Estimated Ras (100 Persons)).
  //
  // The ras is the standard's own main-ingredient figure per 100 persons, so Estimated RM Qty over
  // the ras is how many hundreds of persons the day's main ingredient feeds, and each row's
  // requirement is its own per-100-persons figure against that. The main ingredient comes back as
  // the Estimated RM Qty itself -- est * ras / ras -- which is what it is.
  //
  // The divisor used to be the section's own STD total, which made each row a SHARE of the
  // Estimated RM Qty. That reads the same only where a section's quantities add up to 100 per 100
  // kgs -- which the main ingredients must and the other ingredients need not -- so on a real
  // recipe the requirement jumped the moment anything was typed into 100 Persons QTY, and typing
  // the STD figure straight back landed somewhere else again instead of undoing the edit.
  const reqFromPersons = (row, f) => {
    if (f === null || f === undefined || isNaN(f) || !hasRas) return null;
    return est * (f / ras);
  };

  // 100 Persons QTY, read back out of a typed Planned RM Qty.
  //
  //     100 Persons QTY = Planned RM Qty * Estimated Ras / Estimated RM Qty
  //
  // which is Req. RM Qty's own formula rearranged, with the plan standing in for the requirement.
  // The plan and the requirement are one figure now -- see plannedFromPersons -- so this is the
  // exact inverse of a 100 Persons QTY edit: type a 100 Persons QTY, then retype the Planned RM
  // Qty it produced, and the row comes back to where it was.
  //
  // A row with a 100 Persons STD QTY still divides by it against the requirement supplied, which
  // is the same arithmetic wherever the requirement stands at standard; the branch is kept
  // because a row carrying a pinned req_rm_qty from an older save is read against that pin.
  //
  // An ingredient added to the plan alone can have no "100 Persons STD QTY" -- nothing in Recipe
  // Master to read a qty per 100 kgs from -- so it takes the direct form above.
  const personsFromPlanned = (row, l, req) => {
    const s = stdPersonsQty(row);
    if (isNaN(l)) return null;
    if (s === null || !s) {
      // Planned RM Qty = Req. RM Qty = Estimated RM Qty * 100 Persons QTY / ras, rearranged.
      // TEMPORARY -- tracing how each row's 100 Persons QTY is arrived at. Remove when done.
      debugPersons(row, { branch: 'no STD figure', l, s, req, est, total: sectionStdTotal(row), out: (!est || !hasRas) ? null : (l * ras) / est });
      if (!est || !hasRas) return null;
      return (l * ras) / est;
    }
    debugPersons(row, { branch: 'from plan', l, s, req, est, total: sectionStdTotal(row), out: (req === null || !req) ? null : (l * s) / req });
    if (req === null || !req) return null;
    return (l * s) / req;
  };

  // Planned RM Qty, from a typed 100 Persons QTY: the requirement that 100 Persons QTY calls for.
  //
  // It used to be 100 Persons QTY * Req. RM Qty / 100 Persons STD QTY, a scaling ON TOP of the
  // requirement. That was right while the requirement stayed pinned across the edit -- the ratio
  // to the STD figure was the deviation being expressed. It stopped being right when the edit
  // began restating the requirement too, because the same ratio then got applied twice: on મકાઈ
  // તેલ at Est. RM Qty 110 and ras 2.0, typing 1.2 over a STD of 0.600 doubled the requirement to
  // 66 and then doubled that again, planning 132 against a requirement of 66.
  //
  // Typing in 100 Persons QTY says what the row needs per 100 persons. The requirement follows
  // from it, and the plan opens on the requirement -- the same thing a fresh row does.
  const plannedFromPersons = (row, f, req) => {
    if (f === null || isNaN(f) || req === null) return null;
    return req;
  };

  // The figure the Req. RM Qty column shows.
  //
  // Normally it follows 100 Persons QTY. A Planned RM Qty edit PINS it (req_rm_qty) instead:
  // planning more or less than the requirement must not restate the requirement, and that edit
  // moves 100 Persons QTY, which the requirement is otherwise read from. Editing 100 Persons QTY
  // replaces the pin with the figure that edit calls for, so the column is never stale.
  //
  // A row nobody has typed into reads the STD figure beside it, so this comes out at the standard
  // requirement -- the same figure freshReqRmQty writes, and the same one that typing that STD
  // figure back into the box returns to.
  const reqRmQty = (row) => {
    if (row?.req_rm_qty !== null && row?.req_rm_qty !== undefined && row?.req_rm_qty !== '') {
      const v = parseFloat(row.req_rm_qty);
      if (!isNaN(v)) return v;
    }
    return reqFromPersons(row, personsQty(row));
  };

  return {
    est, ras, hasRas, ingredientRows, stdPersonsQty, personsQty, sectionStdTotal,
    reqFromPersons, personsFromPlanned, plannedFromPersons, reqRmQty,
  };
};

// "100 Persons QTY" -- the figure the rest of the row is read from. An edit here moves Req. RM
// Qty and Planned RM Qty with it (% Weightage follows on its own, being read straight off 100
// Persons QTY):
//
//     Req. RM Qty    = Estimated RM Qty * 100 Persons QTY / Estimated Ras (100 Persons)
//     Planned RM Qty = 100 Persons QTY * Req. RM Qty / 100 Persons STD QTY
//
// In that order: the plan is worked out against the requirement this edit has just set, not the
// one it replaced.
export const applyPersonsQtyEdit = (math, rows, target, value) => {
  if (!target) return rows;
  // Read through personsQty so that clearing the box falls back to the STD figure beside it --
  // the same figure the box then displays -- rather than leaving the row on its old one.
  const f = math.personsQty({ ...target, persons_qty: value });
  const req = math.reqFromPersons(target, f);
  const l = math.plannedFromPersons(target, f, req);

  return rows.map((item) => {
    if (item.ingredient_id !== target.ingredient_id) return item;
    const next = { ...item, persons_qty: value };
    if (req !== null) next.req_rm_qty = Number(req.toFixed(3));
    if (l !== null) {
      next.planned_qty = Number(l.toFixed(3));
      // Reqd Qty / 100 kg, kept in step with Planned RM Qty -- it is that figure per 100 kgs,
      // and other screens read it.
      if (math.est > 0) next.required_qty = parseFloat(((l * 100) / math.est).toFixed(2));
    }
    return next;
  });
};

// What a Planned RM Qty edit does to the row it was typed into.
//
//     100 Persons QTY = Planned RM Qty * 100 Persons STD QTY / Req. RM Qty   (the requirement
//                                                                             as it stood)
//     Req. RM Qty     = Estimated RM Qty * 100 Persons QTY / Estimated Ras
//
// In that order, and the requirement the first line divides by is the STANDARD one -- the row's
// own STD figure put through the second line -- not whatever Req. RM Qty is showing. It has to be
// a fixed base, because the requirement moves with this edit: dividing by the figure the last
// edit left would compound, and typing 30, then 33, then 30 again gave three different
// requirements while retyping the same 30 moved the row on its own. Against the standard the two
// lines collapse to
//
//     100 Persons QTY = Planned RM Qty * Estimated Ras / Estimated RM Qty
//
// so Req. RM Qty comes out equal to the plan that was typed, every time and in any order.
//
// A row with no STD figure needs no special case: personsFromPlanned answers it with the same
// direct form, and putting that through the formula gives the plan straight back.
const plannedEditFields = (math, row, value) => {
  const base = math.reqFromPersons(row, math.stdPersonsQty(row));
  const l = parseFloat(value);
  const f = math.personsFromPlanned(row, l, base);
  const rNext = math.reqFromPersons(row, f);

  const next = { planned_qty: value };
  if (math.est > 0) {
    next.required_qty = parseFloat(((parseFloat(value || 0) * 100) / math.est).toFixed(2));
  }
  if (rNext !== null) next.req_rm_qty = Number(rNext.toFixed(3));
  if (f !== null) next.persons_qty = String(Number(f.toFixed(3)));
  return next;
};

// "Planned RM Qty".
//
// On an OTHER ingredient this touches its own row and nothing else.
//
// On a MAIN ingredient it also carries the OTHER INGREDIENTS with it. The main ingredient is what
// the batch is made of, so its Planned RM Qty is the size of the batch, and each other ingredient
// keeps its share of that batch:
//
//     Planned RM Qty = (its own Planned RM Qty / Total of Planned RM Qty)
//                      * the new Total of Planned RM Qty
//
// both totals read off the MAIN section, the second being what that total becomes once the typed
// figure replaces the one it is replacing. Which is to say every other ingredient is multiplied
// by new total / old total.
//
// It scales each row's OWN PLAN, not its standard. That is the whole point: a row planned away
// from standard stays planned away from standard. શક્કરીયાં 391.95 of a main total of 585, with
// સિંગતેલ hand-raised to 70.2 against a standard 58.5, becomes 491.95 -- a new main total of 685
// -- and સિંગતેલ goes to (70.2 / 585) * 685 = 82.200, still a fifth above its standard. Rebuilt
// from the STD column instead it would have snapped back to 68.500 and the hand adjustment would
// have been lost.
//
// The OTHER MAINS are left alone. A recipe with two mains has two things the batch is made of,
// and setting the quantity of one says nothing about the other -- only the main typed into moves,
// along with the ingredients that season the batch as a whole. That is also what makes the new
// total well defined: it is the old total with one row swapped out.
//
// Each row then goes through plannedEditFields, so 100 Persons QTY, % Weightage and Req. RM Qty
// follow from the new plan exactly as they do for a single row.
export const applyPlannedQtyEdit = (math, rows, target, value) => {
  if (!target) return rows;

  const l = parseFloat(value);
  const plannedOf = (item) => {
    const p = parseFloat(item?.planned_qty);
    return isNaN(p) ? 0 : p;
  };
  // The Total of Planned RM Qty on the main section, as it stands and as this edit leaves it.
  // Only the row typed into moves, so the new total is the old one with that row swapped out.
  const mainTotalBefore = math.ingredientRows
    .filter((r) => r.is_main)
    .reduce((t, r) => t + plannedOf(r), 0);
  const mainTotalAfter = mainTotalBefore - plannedOf(target) + (isNaN(l) ? 0 : l);

  // Only a main resizes the recipe, and only when there is a total to take shares of.
  const resizesRecipe = !!target.is_main && !isNaN(l) && mainTotalBefore > 0;
  const scale = resizesRecipe ? mainTotalAfter / mainTotalBefore : null;

  // The Planned RM Qty each row is to take: the typed string on the row typed into, its own plan
  // scaled on the other ingredients, and null for a row this edit does not reach.
  const plannedFor = (item) => {
    if (item.ingredient_id === target.ingredient_id) return value;
    if (!resizesRecipe) return null;
    // Another main is a separate thing the batch is made of, not a share of this one.
    if (item.is_main) return null;
    const p = parseFloat(item?.planned_qty);
    // Nothing planned yet, so there is no share to carry -- leave the row as it is rather than
    // writing a 0 into it.
    if (isNaN(p)) return null;
    return Number((p * scale).toFixed(3));
  };

  return rows.map((item) => {
    if (!item || REMARK_NAMES.includes(item.name)) return item;
    const planned = plannedFor(item);
    if (planned === null) return item;
    return { ...item, ...plannedEditFields(math, item, planned) };
  });
};

// A row nobody has planned yet -- a recipe opened on this screen for the first time, or an
// ingredient the master has that the day's plan does not.
//
//     Req. RM Qty    = Estimated RM Qty * (100 Persons QTY / Estimated Ras (100 Persons))
//     Planned RM Qty = Req. RM Qty
//
// On such a row 100 Persons QTY is the STD figure beside it, and that figure is this same ras
// against the ingredient's qty per 100 kgs -- so the ras cancels and what is left is the recipe
// scaled to the day's Estimated RM Qty:
//
//     est * (ras * q / 100) / ras = est * q / 100
//
// which is the figure Planned RM Qty has always opened on. It does not matter which ras the row
// is read against, its own or the day's estimate: both give the same requirement, so a recipe
// with a Std. RAS QTY of its own and one taking the day's agree here.
//
// This is reqFromPersons at the row's STD figure, written out here only because the rows are
// built before there is a math object to ask. It fills in Planned RM Qty alone: the requirement
// needs no pin, because reqRmQty reaches the same figure by reading the row's untouched 100
// Persons QTY -- and a pin would make an untouched row read back as a planned one.
export const freshReqRmQty = (row, { estRM, rasValue }) => {
  const est = parseFloat(estRM) || 0;
  const ras = parseFloat(rasValue);
  const q = parseFloat(row?.stdQty);
  if (!est || isNaN(ras) || !ras || isNaN(q)) return null;
  const stdPersons = ras * (q / 100);
  return Number(((est * stdPersons) / ras).toFixed(3));
};

// Planned RM Qty and 100 Persons QTY are read back out of Reqd Qty / 100 kg.
//
// rm_receipe keeps std_qty and required_qty and nothing else, so the Event screen's two editable
// columns have no column of their own to be stored in. They do not need one: Planned RM Qty is
// Reqd Qty / 100 kg against the slot's Estimated RM Qty, which is the same relation the edits
// above maintain in the other direction, and 100 Persons QTY follows from it the way a Planned RM
// Qty edit moves it. Only the second decimal place of required_qty is lost in the round trip.
export const rebuildFromRequiredQty = (rows, { estRM, rasValue }) => {
  const math = planningMath({ rows, estRM, rasValue });
  if (!(math.est > 0)) return rows;
  return rows.map((item) => {
    if (!item || REMARK_NAMES.includes(item.name)) return item;
    if (item.planned_qty !== undefined && item.planned_qty !== null && item.planned_qty !== '') return item;
    const required = parseFloat(item.required_qty);
    if (isNaN(required)) return item;
    const planned = (required * math.est) / 100;
    const req = math.reqFromPersons(item, math.personsQty(item));
    const persons = math.personsFromPlanned(item, planned, req);
    const next = { ...item, planned_qty: Number(planned.toFixed(3)) };
    if (persons !== null) next.persons_qty = String(Number(persons.toFixed(3)));
    return next;
  });
};

// A saved Planned RM Qty with no 100 Persons QTY beside it.
//
// The same problem the rebuild above solves for the Event screen, on the rows Recipe Planning
// saved before it had columns of its own to keep the other two figures in: the plan came back and
// 100 Persons QTY -- and the % Weightage read off it -- came back at their STANDARD figures next
// to a plan that is not standard, which is the very link the planner had used to set it. The pair
// is invertible, so the plan is enough to put them back:
//
//     100 Persons QTY = Planned RM Qty * 100 Persons STD QTY / Req. RM Qty
//
// with Req. RM Qty as it stands at standard -- the figure a Planned RM Qty edit pins.
//
// A row whose plan IS the standard one is left alone: its 100 Persons QTY is already the STD
// figure the column falls back to, and writing it in would turn an untouched row into a touched
// one for no change in what is shown.
export const restorePersonsFromPlanned = (rows, { estRM, rasValue }) => {
  const math = planningMath({ rows, estRM, rasValue });
  if (!(math.est > 0)) return rows;
  return rows.map((item) => {
    if (!item || REMARK_NAMES.includes(item.name)) return item;
    if (item.persons_qty !== undefined && item.persons_qty !== null && item.persons_qty !== '') {
      // TEMPORARY -- the row is NOT worked out on this load. Remove with debugPersons.
      /* eslint-disable-next-line no-console */
      console.log(`[100 Persons QTY] ${item.name} (id ${item.ingredient_id}) READ FROM DB: persons_qty=${item.persons_qty}, req_rm_qty=${item.req_rm_qty ?? 'null'}, planned=${item.planned_qty} -- no formula applied; STD beside it is live (${math.stdPersonsQty(item)})`);
      return item;
    }
    const planned = parseFloat(item.planned_qty);
    if (isNaN(planned)) return item;
    // The row's OWN requirement where it carries one -- a row opened fresh does, and its plan is
    // that requirement, so there is nothing to restore. Recomputing the requirement here instead
    // measured the plan against a figure the row was never planned against, and every untouched
    // row came back reading as a deviation. Where there is no pin this is the standard
    // requirement, which is what the rows this restore exists for are measured against.
    const req = math.reqRmQty(item);
    const persons = math.personsFromPlanned(item, planned, req);
    if (persons === null) return item;
    const std = math.stdPersonsQty(item);
    if (std !== null && Math.abs(persons - std) < 0.0005) return item;
    const next = { ...item, persons_qty: String(Number(persons.toFixed(3))) };
    if (req !== null && (item.req_rm_qty === undefined || item.req_rm_qty === null || item.req_rm_qty === '')) {
      next.req_rm_qty = Number(req.toFixed(3));
    }
    return next;
  });
};

// ---- The Recipe Planning export ----
//
// One sheet per maharaj, as it has always been, but every sheet now carries the screen's columns
// and not just the recipe's four. Built here rather than in the page so it reads through the same
// planningMath the table renders from: a sheet and the screen cannot then drift apart.
//
// Only ONE maharaj's plan is ever on screen. That maharaj's rows are taken whole from it, so the
// sheet prints what is showing, edits and all. Every other maharaj is worked out at standard --
// what their recipe would open on against the day's Estimated RM Qty -- which is a real figure
// for that recipe, not a blank column and not the planned maharaj's numbers wearing their name.
//
// Each sheet is scaled by its OWN ras: that maharaj's Std. RAS QTY where Recipe Master gives one,
// else the day's estimate. The screen makes the same choice; here it is made once per recipe.
export const EXPORT_HEADER = [
  "Id", "Ingredients", "Qty/100kg", "Unit", "Main Item",
  "100 Persons STD QTY", "100 Persons QTY", "% Weightage", "Req. RM Qty", "Planned RM Qty",
];

const EXPORT_COL_WIDTHS = [
  { wch: 5 }, { wch: 30 }, { wch: 11 }, { wch: 7 }, { wch: 10 },
  { wch: 19 }, { wch: 17 }, { wch: 12 }, { wch: 13 }, { wch: 15 },
];

// ingredient + eng_name, the way every other label on this screen is built.
const exportName = (r) => (r?.ingredient || r?.eng_name)
  ? `${r?.ingredient ?? ''}${r?.eng_name ? ` (${r.eng_name})` : ''}`.trim()
  : '-';

export const buildRecipeExportSheets = (exportRows, {
  estRM, itemDisplayName, plannedMaharajId, plannedRows, dayRas,
}) => {
  // Group by maharaj_name, preserving the order the query returned them in.
  const byMaharaj = (exportRows ?? []).reduce((acc, r) => {
    (acc[r?.maharaj_name] = acc[r?.maharaj_name] ?? []).push(r);
    return acc;
  }, {});

  const plannedByIngredient = new Map(
    (plannedRows ?? [])
      .filter((r) => r && !REMARK_NAMES.includes(r.name))
      .map((r) => [String(r.ingredient_id), r])
  );

  return Object.entries(byMaharaj).map(([maharaj, ingredients]) => {
    const isPlanned = plannedMaharajId != null
      && String(ingredients[0]?.maharaj_id) === String(plannedMaharajId);

    const ownRas = parseFloat(ingredients[0]?.std_ras);
    const sheetRas = (!isNaN(ownRas) && ownRas !== 0) ? ownRas : parseFloat(dayRas);

    const rows = ingredients.map((ing) => {
      const live = isPlanned ? plannedByIngredient.get(String(ing.ingredient_id)) : null;
      if (live) return live;
      const stdQty = parseFloat(ing.qty);
      return {
        ingredient_id: ing.ingredient_id,
        name: ing.ingredient,
        stdQty,
        is_main: !!ing.is_main,
        persons_qty: null,
        planned_qty: freshReqRmQty({ stdQty }, { estRM, rasValue: sheetRas }),
      };
    });

    const math = planningMath({ rows, estRM, rasValue: sheetRas });
    // % Weightage is a share of the row's OWN section, the same split the table totals by.
    const weightage = (row) => {
      const f = math.personsQty(row);
      const total = math.ingredientRows
        .filter((r) => !!r.is_main === !!row?.is_main)
        .reduce((t, r) => t + (math.stdPersonsQty(r) ?? 0), 0);
      return (f === null || !total) ? '' : `${((f / total) * 100).toFixed(2)}%`;
    };

    // Row order is the one this export has always used -- the query's -- so a sheet someone
    // already reads keeps its shape and only gains columns on the right.
    const body = ingredients.map((ing, index) => {
      const row = rows[index];
      return [
        index + 1,
        exportName(ing),
        ing.qty,
        ing.unit,
        row.is_main ? 'Yes' : 'No',
        dec3(math.stdPersonsQty(row)),
        dec3(math.personsQty(row)),
        weightage(row),
        dec3(math.reqRmQty(row)),
        dec3(parseFloat(row.planned_qty)),
      ];
    });

    return {
      name: maharaj,
      cols: EXPORT_COL_WIDTHS,
      aoa: [['Item Name:', itemDisplayName], [], EXPORT_HEADER, ...body],
    };
  });
};

// One row per ingredient, a section band above each group and a subtotal row below it.
//
// mainItemEditable: Recipe Planning stores the tick against the day's plan, so it can be changed
// there. The Event screen has nowhere to keep it -- rm_receipe has no is_main column -- so it
// shows Recipe Master's answer and does not pretend the box can be changed.
export function RecipePlanningTable({
  math,
  disabled = false,
  onPersonsQtyChange,
  onPlannedQtyChange,
  onIsMainChange,
  mainItemEditable = true,
}) {
  const { ingredientRows, stdPersonsQty, personsQty, reqRmQty } = math;
  const mainRows = ingredientRows.filter((r) => r.is_main);
  const otherRows = ingredientRows.filter((r) => !r.is_main);

  // % Weightage divides by the STD total of its OWN section, which is what the sheet's figures
  // do -- the main rows and the other rows are each weighed against their own total, so at
  // standard each set sums to 100%.
  const sumStd = (list) => list.reduce((t, r) => t + (stdPersonsQty(r) ?? 0), 0);
  const sum = (list, fn) => list.reduce((t, r) => t + (fn(r) ?? 0), 0);
  const weightage = (row, total) => {
    const f = personsQty(row);
    if (f === null || !total) return '';
    return `${((f / total) * 100).toFixed(2)}%`;
  };
  // The Total row is the column it heads, added up. A flat 100.00% is only true while every 100
  // Persons QTY still sits at its STD figure, and a Total that says otherwise hides exactly the
  // change the planner just made.
  const weightageTotal = (list, total) => {
    if (!total) return '';
    return `${((sum(list, personsQty) / total) * 100).toFixed(2)}%`;
  };

  // COL_COUNT is the single source of truth for how wide the section bands span, so the header,
  // the bands and the body cannot drift apart.
  const COL_COUNT = 8;
  const renderSection = (title, list) => {
    const sectionStdTotal = sumStd(list);
    return (
      <React.Fragment key={title}>
        <tr className="bg-gray-200 font-bold">
          <td className="text-left px-3 py-1" colSpan={COL_COUNT}>{title}</td>
        </tr>
        {list.map((item) => {
          const idx = ingredientRows.indexOf(item);
          return (
            <tr key={item.ingredient_id} className={`border ${item.is_main ? 'bg-blue-50' : ''}`}>
              <td className="text-center">{item.name}</td>
              <td className="text-center">{item.unit?.name ?? "-"}</td>
              <td className="text-center">
                <input
                  type="checkbox"
                  checked={item.is_main || false}
                  disabled={!mainItemEditable}
                  onChange={(e) => onIsMainChange && onIsMainChange(item.ingredient_id, e.target.checked)}
                  className={`w-4 h-4 accent-blue-500 ${mainItemEditable ? 'cursor-pointer' : ''}`}
                />
              </td>
              <td className="text-center p-1">{dec3(stdPersonsQty(item))}</td>
              <td className="text-center p-1">
                <input
                  type="number" step="0.001" onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }}
                  id={`persons_qty_${idx}`} name="persons_qty" disabled={disabled}
                  placeholder="100 Persons QTY"
                  value={item.persons_qty ?? (stdPersonsQty(item) === null ? '' : dec3(stdPersonsQty(item)))}
                  onChange={(e) => onPersonsQtyChange(e, idx)}
                  className={`w-24 p-1 border rounded-md placeholder:text-sm ${disabled ? "" : "bg-orange-50"}`} />
              </td>
              <td className="text-center p-1">{weightage(item, sectionStdTotal)}</td>
              <td className="text-center p-1">{dec3(reqRmQty(item))}</td>
              <td className="text-center p-1">
                <input
                  type="number" onWheel={(e) => e.target.blur()}
                  onKeyDown={(e) => { if (e.key === 'ArrowUp' || e.key === 'ArrowDown') { e.preventDefault(); } }}
                  step="0.01" id={`planned_qty_${idx}`} disabled={disabled}
                  placeholder="Planned RM Qty" name="planned_qty"
                  value={item.planned_qty ?? 0} onChange={(e) => onPlannedQtyChange(e, idx)}
                  className={`w-24 p-1 border rounded-md placeholder:text-sm ${disabled ? "" : "bg-orange-50"}`} />
              </td>
            </tr>
          );
        })}
        <tr className="border bg-gray-100 font-bold">
          <td className="text-center px-3" colSpan="3">Total</td>
          <td className="text-center px-3">{dec3(sectionStdTotal)}</td>
          <td className="text-center px-3">{dec3(sum(list, personsQty))}</td>
          <td className="text-center px-3">{weightageTotal(list, sectionStdTotal)}</td>
          <td className="text-center px-3">{dec3(sum(list, reqRmQty))}</td>
          <td className="text-center px-3">{sum(list, (r) => parseFloat(r.planned_qty ?? 0)).toFixed(3)}</td>
        </tr>
      </React.Fragment>
    );
  };

  return (
    <div className="border p-2">
      <table className="border space-x-1">
        <thead className="space-x-1">
          <tr>
            <th className="px-3">Main Ingredient</th>
            <th className="px-3">Unit</th>
            <th className="px-3">Main Item</th>
            <th className="px-3">100 Persons STD QTY</th>
            <th className="px-3">100 Persons QTY</th>
            <th className="px-3">% Weightage</th>
            <th className="px-3">Req. RM Qty</th>
            <th className="px-3">Planned RM Qty</th>
          </tr>
        </thead>
        <tbody>
          {mainRows.length > 0 && renderSection('Main Ingredients', mainRows)}
          {otherRows.length > 0 && renderSection('Other Ingredients', otherRows)}
        </tbody>
      </table>
    </div>
  );
}
